import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../CSS/mapview.css';
import '../CSS/utilityMap.css';
import { useTheme } from '../ThemeContext';
import { useWindowWidth } from '../lib/useWindowWidth';
import { clearUtilityDataCache, loadUtilityData } from '../lib/utilityData';
import {
  buildUtilityRecords,
  rankUtilityRecords,
  stackSmallTerritoriesOnTop,
  summarizeUtilityRecords
} from '../lib/utilityView';

const TILE_LAYERS = {
  dark: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=cb1_2l38_1_67edb4a0fb30722954efd14d',
  light: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_2l38_1_67edb4a0fb30722954efd14d'
};

const MAP_COLORS = [
  '#22c55e', '#84cc16', '#a3e635', '#fbbf24', '#f59e0b',
  '#fb923c', '#f87171', '#ef4444', '#dc2626', '#991b1b'
];

const escapeHtml = (value) => String(value ?? '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(`${value.slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(date);
};

const getMapColor = (value, maximum) => {
  if (!Number.isFinite(value) || value <= 0 || maximum <= 0) return MAP_COLORS[0];
  const normalized = Math.min(value / maximum, 1);
  const index = Math.min(Math.floor(normalized * MAP_COLORS.length), MAP_COLORS.length - 1);
  return MAP_COLORS[index];
};

const UtilityMapVisual = ({
  stateId,
  selectedDay,
  currentMetric,
  setCurrentMetric,
  startDate,
  endDate,
  timezone
}) => {
  const { theme } = useTheme();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const tileLayer = useRef(null);
  const territoryLayer = useRef(null);
  const featureStyleRef = useRef(null);
  const hasFittedBounds = useRef(false);
  const initialTheme = useRef(theme);
  const isMobileViewport = useWindowWidth() < 768;

  const [utilityData, setUtilityData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [viewMode, setViewMode] = useState('percent');
  const [selectedUtilityId, setSelectedUtilityId] = useState(null);
  const [isSummarySheetOpen, setIsSummarySheetOpen] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);
    setUtilityData(null);
    setSelectedUtilityId(null);
    setIsSummarySheetOpen(false);
    hasFittedBounds.current = false;

    if (mapInstance.current) {
      if (territoryLayer.current) {
        territoryLayer.current.removeFrom(mapInstance.current);
        territoryLayer.current = null;
      }
      mapInstance.current.setMaxBounds(null);
      mapInstance.current.setMinZoom(0);
    }

    loadUtilityData({ stateId, startDate, endDate, timezone })
      .then((data) => {
        if (isMounted) setUtilityData(data);
      })
      .catch((loadError) => {
        console.error('Failed to load utility data:', loadError);
        if (isMounted) setError(loadError.message || 'Utility data failed to load.');
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [stateId, startDate, endDate, timezone, reloadKey]);

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return undefined;

    const map = L.map(mapRef.current, {
      center: [39, -98],
      zoom: 4,
      zoomControl: false,
      attributionControl: false
    });
    mapInstance.current = map;

    L.control.zoom({ position: 'topright' }).addTo(map);
    const attribution = L.control.attribution({ position: 'bottomright' });
    attribution.setPrefix('');
    attribution.addTo(map);

    tileLayer.current = L.tileLayer(TILE_LAYERS[initialTheme.current], {
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
      subdomains: 'abcd',
      maxZoom: 19,
      crossOrigin: true
    }).addTo(map);

    map.on('click', (e) => {
      if (e.originalEvent?._handledByFeature) return;
      setSelectedUtilityId(null);
    });
    setIsMapReady(true);

    const resizeTimer = window.setTimeout(() => map.invalidateSize(), 0);
    return () => {
      window.clearTimeout(resizeTimer);
      map.remove();
      mapInstance.current = null;
      tileLayer.current = null;
      territoryLayer.current = null;
      hasFittedBounds.current = false;
    };
  }, []); // Leaflet owns this DOM node until the component unmounts.

  useEffect(() => {
    if (tileLayer.current) tileLayer.current.setUrl(TILE_LAYERS[theme]);
  }, [theme]);

  const utilityRecords = useMemo(() => {
    if (!utilityData) return [];

    return buildUtilityRecords({
      companies: utilityData.companies,
      predictions: utilityData.predictions,
      selectedDay,
      metric: currentMetric
    });
  }, [utilityData, selectedDay, currentMetric]);

  const visibleUtilityRecords = useMemo(
    () => utilityRecords.filter((record) => record.hasPrediction && record.value > 0),
    [utilityRecords]
  );

  const recordsById = useMemo(
    () => Object.fromEntries(visibleUtilityRecords.map((record) => [record.id, record])),
    [visibleUtilityRecords]
  );

  const visibleGeoJson = useMemo(() => {
    if (!utilityData?.geoJson) return null;
    const visibleIds = new Set(visibleUtilityRecords.map((record) => record.id));
    const features = utilityData.geoJson.features.filter((feature) => (
      visibleIds.has(String(feature.properties.company_id))
    ));
    return {
      ...utilityData.geoJson,
      metadata: { ...utilityData.geoJson.metadata, utility_count: features.length },
      features
    };
  }, [utilityData, visibleUtilityRecords]);

  const selectedUtility = selectedUtilityId ? recordsById[selectedUtilityId] || null : null;
  const rankedUtilities = useMemo(
    () => rankUtilityRecords(visibleUtilityRecords),
    [visibleUtilityRecords]
  );
  const maximumMapValue = useMemo(() => {
    if (visibleUtilityRecords.length === 0) return 0;
    if (viewMode === 'percent') {
      return 40;
    }
    return Math.max(...visibleUtilityRecords.map((record) => record.value), 1);
  }, [visibleUtilityRecords, viewMode]);

  const statewideSummary = useMemo(() => {
    return summarizeUtilityRecords(utilityRecords, selectedDay);
  }, [utilityRecords, selectedDay]);

  useEffect(() => {
    if (selectedUtilityId && !recordsById[selectedUtilityId]) {
      setSelectedUtilityId(null);
      setIsSummarySheetOpen(false);
    }
  }, [selectedUtilityId, recordsById]);

  const getFeatureStyle = useCallback((feature) => {
    const utilityId = String(feature.properties.company_id);
    const record = recordsById[utilityId];
    const mapValue = viewMode === 'percent' ? record?.percentOut : record?.value;
    const isSelected = selectedUtilityId === utilityId;

    return {
      color: isSelected ? '#197ccf' : theme === 'dark' ? '#b8c2cc' : '#475569',
      weight: isSelected ? 2.4 : 0.7,
      opacity: isSelected ? 1 : 0.78,
      fillColor: getMapColor(mapValue || 0, maximumMapValue),
      fillOpacity: isSelected ? 0.88 : 0.68
    };
  }, [recordsById, viewMode, selectedUtilityId, theme, maximumMapValue]);

  useEffect(() => {
    featureStyleRef.current = getFeatureStyle;
    if (territoryLayer.current) {
      territoryLayer.current.options.style = getFeatureStyle;
      territoryLayer.current.setStyle(getFeatureStyle);
    }
  }, [getFeatureStyle]);

  useEffect(() => {
    if (!isMapReady || !mapInstance.current || !visibleGeoJson) return;

    if (territoryLayer.current) {
      territoryLayer.current.removeFrom(mapInstance.current);
      territoryLayer.current = null;
    }

    if (visibleGeoJson.features.length === 0) return;

    territoryLayer.current = L.geoJSON(visibleGeoJson, {
      style: (feature) => featureStyleRef.current?.(feature) || {},
      onEachFeature: (feature, layer) => {
        const utilityId = String(feature.properties.company_id);
        const record = recordsById[utilityId];
        if (!record) return;

        const metricLabel = `${currentMetric.charAt(0).toUpperCase()}${currentMetric.slice(1)} estimate`;
        layer.bindTooltip(`
          <div class="utility-map-tooltip">
            <strong>${escapeHtml(record.shortName)}</strong>
            <span>${escapeHtml(metricLabel)}</span>
            <b>${record.value.toLocaleString()} customers out</b>
            <span>${record.percentOut.toFixed(2)}% impact</span>
            <small>${escapeHtml(formatDate(record.date))}</small>
          </div>
        `, {
          direction: 'top',
          className: 'custom-dark-tooltip',
          opacity: 1
        });

        layer.on({
          click: (event) => {
            if (event.originalEvent) {
              event.originalEvent._handledByFeature = true;
              L.DomEvent.stopPropagation(event.originalEvent);
            }
            setSelectedUtilityId(utilityId);
            setIsSummarySheetOpen(true);
          },
          mouseover: () => {
            layer.setStyle({ weight: 2, fillOpacity: 0.88 });
            layer.bringToFront();
          },
          mouseout: () => {
            if (territoryLayer.current) {
              territoryLayer.current.resetStyle(layer);
              stackSmallTerritoriesOnTop(territoryLayer.current.getLayers());
            }
          }
        });
      }
    }).addTo(mapInstance.current);

    stackSmallTerritoriesOnTop(territoryLayer.current.getLayers());

    if (!hasFittedBounds.current) {
      const bounds = L.geoJSON(utilityData.geoJson).getBounds();
      if (bounds.isValid()) {
        mapInstance.current.fitBounds(bounds, { padding: [12, 12] });
        mapInstance.current.setMaxBounds(bounds.pad(0.12));
        mapInstance.current.setMinZoom(mapInstance.current.getBoundsZoom(bounds, false));
        hasFittedBounds.current = true;
      }
    }

    const resizeTimer = window.setTimeout(() => mapInstance.current?.invalidateSize(), 0);
    return () => window.clearTimeout(resizeTimer);
  }, [
    isMapReady,
    utilityData,
    visibleGeoJson,
    recordsById,
    currentMetric
  ]);

  useEffect(() => {
    if (selectedUtilityId) setIsSummarySheetOpen(true);
  }, [selectedUtilityId]);

  const handleRetry = () => {
    clearUtilityDataCache();
    setReloadKey((key) => key + 1);
  };

  const summaryRecord = selectedUtility || statewideSummary;
  const summaryTitle = selectedUtility
    ? `${selectedUtility.shortName} Summary`
    : `${utilityData?.state?.name || 'State'} Utility Summary`;

  const summary = (
    <UtilitySummary
      title={summaryTitle}
      record={summaryRecord}
      selectedUtility={selectedUtility}
      selectedUtilityId={selectedUtilityId}
      setSelectedUtilityId={setSelectedUtilityId}
      rankedUtilities={rankedUtilities}
      metric={currentMetric}
    />
  );

  return (
    <div className="container-fluid mt-2 map-visual is-state-view utility-map-visual">
      <div className="row">
        <div className="col-12 col-lg-8 map-visual-main">
          <div className="card border-secondary shadow-sm map-visual-card">
            <div className="card-header border-secondary bg-transparent map-visual-header">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-1 map-visual-toolbar">
                <div className="d-flex align-items-center map-toolbar-mode utility-map-mode">
                  <span className="utility-map-context">
                    <i className="fas fa-bolt" aria-hidden="true"></i>
                    Utility forecast
                  </span>
                </div>

                <div className="d-flex justify-content-center align-items-center map-toolbar-metric">
                  <div className="btn-group me-2" role="group" aria-label="Utility estimate">
                    {['min', 'mean', 'max'].map((metric) => (
                      <button
                        key={metric}
                        type="button"
                        className={`btn ${currentMetric === metric ? 'btn-secondary active' : 'btn-outline-secondary'}`}
                        onClick={() => setCurrentMetric(metric)}
                        aria-pressed={currentMetric === metric}
                      >
                        <i className={`fas fa-${metric === 'min' ? 'arrow-down' : metric === 'max' ? 'arrow-up' : 'equals'} me-1`} aria-hidden="true"></i>
                        {metric.charAt(0).toUpperCase() + metric.slice(1)}
                      </button>
                    ))}
                  </div>
                  <i
                    className="fas fa-info-circle text-muted"
                    title="Min is best case, Mean is expected, and Max is worst case."
                    aria-label="Utility estimate information"
                  ></i>
                </div>

                <div className="d-flex justify-content-end align-items-center map-toolbar-view">
                  <div className="btn-group" role="group" aria-label="Utility map value">
                    <button
                      type="button"
                      className={`btn ${viewMode === 'percent' ? 'btn-custom active' : 'btn-outline-custom'}`}
                      onClick={() => setViewMode('percent')}
                      aria-pressed={viewMode === 'percent'}
                      title="View impact percentage"
                    >
                      <i className="fas fa-percentage" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === 'absolute' ? 'btn-custom active' : 'btn-outline-custom'}`}
                      onClick={() => setViewMode('absolute')}
                      aria-pressed={viewMode === 'absolute'}
                      title="View customers out"
                    >
                      <i className="fas fa-hashtag" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-body p-0 position-relative map-visual-body utility-map-body">
              <div
                ref={mapRef}
                className="map-canvas"
                role="region"
                aria-label={`${utilityData?.state?.name || 'State'} utility forecast map`}
              ></div>

              {!isLoading && !error && visibleUtilityRecords.length > 0 && (
                <div className="utility-map-legend" aria-hidden="true">
                  <strong className="utility-map-legend-title">
                    {`Customers Out (${currentMetric})`}
                  </strong>
                  <div className="utility-map-legend-range">
                    <small>0{viewMode === 'percent' ? '%' : ''}</small>
                    <small>
                      {viewMode === 'percent'
                        ? '40%+'
                      : Math.round(maximumMapValue).toLocaleString()}
                    </small>
                  </div>
                  <div className="utility-map-legend-scale"></div>
                </div>
              )}

              {isLoading && (
                <div className="utility-map-status" role="status">
                  <div className="spinner-border text-primary" aria-hidden="true"></div>
                  <span>Loading utility data...</span>
                </div>
              )}

              {error && (
                <div className="utility-map-status" role="alert">
                  <i className="fas fa-exclamation-circle text-danger" aria-hidden="true"></i>
                  <span>{error}</span>
                  <button type="button" className="btn btn-outline-primary" onClick={handleRetry}>
                    Retry
                  </button>
                </div>
              )}

              {!isLoading && !error && utilityData && visibleUtilityRecords.length === 0 && (
                <div className="utility-map-status" role="status">
                  <i className="fas fa-info-circle text-muted" aria-hidden="true"></i>
                  <span>No utility outage predictions are available for this date and estimate.</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-4 map-summary-desktop">
          {summary}
        </div>
      </div>

      {isMobileViewport && (
        <div className={`map-summary-sheet ${isSummarySheetOpen ? 'is-open' : ''}`} aria-label="Utility summary">
          <button
            type="button"
            className="map-summary-sheet-toggle"
            onClick={() => setIsSummarySheetOpen((open) => !open)}
            aria-controls="utility-summary-sheet-content"
            aria-expanded={isSummarySheetOpen}
          >
            <span className="map-summary-sheet-handle" aria-hidden="true"></span>
            <span className="map-summary-sheet-title">{summaryTitle}</span>
            <span className="map-summary-sheet-value">{Math.round(summaryRecord.value || 0).toLocaleString()}</span>
            <i className={`fas fa-chevron-${isSummarySheetOpen ? 'down' : 'up'}`} aria-hidden="true"></i>
          </button>
          <div
            id="utility-summary-sheet-content"
            className="map-summary-sheet-content"
            aria-hidden={!isSummarySheetOpen}
          >
            {summary}
          </div>
        </div>
      )}
    </div>
  );
};

const UtilitySummary = ({
  title,
  record,
  selectedUtility,
  selectedUtilityId,
  setSelectedUtilityId,
  rankedUtilities,
  metric
}) => (
  <div className="d-flex flex-column gap-3 h-100 utility-summary">
    <div className="card shadow-sm flex-grow-1 border-secondary">
      <div className="card-header border-secondary bg-transparent d-flex justify-content-between align-items-center gap-2">
        <h5 className="mb-0 fw-bold text-main">{title}</h5>
      </div>
      <div className="card-body">
        <div className="utility-summary-row">
          <span className="text-muted">Customers Out ({metric} est.)</span>
          <strong>{Math.round(record.value || 0).toLocaleString()}</strong>
        </div>
        <div className="utility-summary-row">
          <span className="text-muted">Impact %</span>
          <strong>{Number(record.percentOut || 0).toFixed(2)}%</strong>
        </div>
        <div className="utility-summary-row">
          <span className="text-muted">Forecast Date</span>
          <strong>{formatDate(record.date)}</strong>
        </div>

        {!selectedUtility && (
          <div className="utility-summary-empty text-center text-muted">
            <i className="fas fa-bolt fa-2x text-secondary opacity-50" aria-hidden="true"></i>
            <p className="mb-0">Select a utility territory for company-level details.</p>
          </div>
        )}
      </div>
    </div>

    <div className="card shadow-sm border-secondary">
      <div className="card-header border-secondary bg-transparent">
        <h6 className="mb-0 fw-bold text-main">Top Utilities by Customers Out</h6>
      </div>
      <div className="card-body p-0">
        <ul className="list-group list-group-flush">
          {rankedUtilities.map((utility, index) => (
            <li
              key={utility.id}
              className={`list-group-item d-flex justify-content-between align-items-center bg-transparent border-secondary utility-rank-item${selectedUtilityId === utility.id ? ' active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedUtilityId(utility.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedUtilityId(utility.id); } }}
              aria-pressed={selectedUtilityId === utility.id}
            >
              <span>{index + 1}. {utility.shortName}</span>
              <span className="bg-primary text-white px-1 py-0.25 rounded-pill">
                {utility.value.toLocaleString()}
              </span>
            </li>
          ))}
          {rankedUtilities.length === 0 && <li className="list-group-item text-muted">No data available</li>}
        </ul>
      </div>
    </div>
  </div>
);

export default UtilityMapVisual;
